import { useState } from 'preact/hooks';
import type { Contact } from '../../types/contact';
import { Modal } from '../common/Modal';
import { deleteContact } from '../../state/contacts.store';
import { Trash2, AlertTriangle, Loader2, MessageSquare, FileText, CheckSquare, FolderGit2 } from 'lucide-preact';

interface DeleteContactModalProps {
  isOpen: boolean;
  contact: Contact | null;
  onClose: () => void;
}

export function DeleteContactModal({ isOpen, contact, onClose }: DeleteContactModalProps) {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  if (!isOpen || !contact) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    const success = await deleteContact(contact);
    setIsDeleting(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidthClass="max-w-md">
      <div class="p-6 space-y-4">
        {/* Header */}
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0">
            <Trash2 class="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h3 class="text-base font-bold text-slate-100">Excluir Conversa</h3>
            <p class="text-xs text-slate-400">Esta ação apaga todo o histórico permanentemente</p>
          </div>
        </div>

        {/* Info & Structured List */}
        <div class="space-y-3">
          <p class="text-xs text-slate-300 leading-relaxed">
            Você está prestes a excluir a conversa de <strong class="text-white font-bold">{contact.name}</strong> ({contact.phone_number}).
          </p>

          <div class="p-3.5 bg-rose-950/40 border border-rose-500/30 rounded-2xl space-y-3">
            <div class="flex items-center gap-2 text-rose-300 text-xs font-bold uppercase tracking-wide">
              <AlertTriangle class="w-4 h-4 shrink-0 text-rose-400" />
              <span>Itens que serão removidos:</span>
            </div>

            <ul class="grid grid-cols-2 gap-2 text-xs">
              <li class="flex items-center gap-2 p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-200">
                <MessageSquare class="w-4 h-4 text-cyan-400 shrink-0" />
                <span class="font-medium text-[11px]">Histórico de Mensagens & Mídias</span>
              </li>
              <li class="flex items-center gap-2 p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-200">
                <FileText class="w-4 h-4 text-amber-400 shrink-0" />
                <span class="font-medium text-[11px]">Resumos Executivos</span>
              </li>
              <li class="flex items-center gap-2 p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-200">
                <CheckSquare class="w-4 h-4 text-emerald-400 shrink-0" />
                <span class="font-medium text-[11px]">Ações Pendentes</span>
              </li>
              <li class="flex items-center gap-2 p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-200">
                <FolderGit2 class="w-4 h-4 text-purple-400 shrink-0" />
                <span class="font-medium text-[11px]">Projetos & Tópicos</span>
              </li>
            </ul>

            <p class="text-[10px] text-rose-300/80 font-mono flex items-center gap-1.5">
              <AlertTriangle class="w-3.5 h-3.5 text-rose-400 shrink-0" />
              <span>Esta operação é irreversível e removerá todos os dados do banco de dados.</span>
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div class="flex items-center justify-end gap-2.5 pt-2">
          <button
            onClick={onClose}
            disabled={isDeleting}
            class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition cursor-pointer disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 border border-rose-400/50 shadow-md shadow-rose-900/40 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <Loader2 class="w-4 h-4 animate-spin text-white" />
                <span>Excluindo...</span>
              </>
            ) : (
              <>
                <Trash2 class="w-4 h-4 text-white" />
                <span>Excluir Conversa</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
